import { AlertTriangle, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import { authedFetch, getToken } from '@/lib/api';
import { isIOS } from '@/lib/native'

interface BillingStatus {
  status: string // 'trialing' | 'active' | 'past_due' | 'canceled' | 'trial_expired'
  trial_ends_at?: string
}

export function BillingBanner() {
  const [billing, setBilling] = useState<BillingStatus | null>(null)
  const [dismissed, setDismissed] = useState(false)

  useEffect(() => {
    const token = getToken()
    if (!token) return
    authedFetch('/api/v1/billing', {}, { redirectOnAuthFailure: false })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setBilling(data) })
      .catch(() => {})
  }, [])

  // App Store 3.1.1 — no upgrade / payment CTAs on iOS. Users manage
  // billing on usetenon.com.
  if (isIOS()) return null
  if (!billing || dismissed) return null

  // Determine banner config
  let config: { msg: string; color: string; dismissable: boolean; cta: string } | null = null

  if (billing.status === 'past_due') {
    config = {
      msg: 'Payment failed. Update your payment method to keep your account active.',
      color: 'red',
      dismissable: false,
      cta: 'Update Payment',
    }
  } else if (billing.status === 'canceled') {
    config = {
      msg: 'Your subscription is inactive. Reactivate to continue.',
      color: 'red',
      dismissable: false,
      cta: 'Reactivate',
    }
  } else if (billing.status === 'trialing' && billing.trial_ends_at) {
    const days = Math.ceil((new Date(billing.trial_ends_at).getTime() - Date.now()) / 86400000)
    if (days <= 0) {
      config = {
        msg: 'Your trial has expired. Upgrade to continue.',
        color: 'red',
        dismissable: false,
        cta: 'Upgrade Now',
      }
    } else if (days <= 1) {
      config = {
        msg: 'Trial ends tomorrow. Upgrade to keep access.',
        color: 'orange',
        dismissable: true,
        cta: 'Upgrade',
      }
    } else if (days <= 3) {
      config = {
        msg: `${days} days left in your trial.`,
        color: 'orange',
        dismissable: true,
        cta: 'Upgrade',
      }
    } else if (days <= 7) {
      config = {
        msg: `${days} days left in your trial.`,
        color: 'yellow',
        dismissable: true,
        cta: 'Upgrade',
      }
    }
  }

  if (!config) return null

  const colors: Record<string, string> = {
    red: 'bg-red-600 text-white',
    orange: 'bg-orange-500 text-white',
    yellow: 'bg-yellow-500 text-black',
  }

  return (
    <div
      className={`flex items-center justify-between px-4 py-2 text-sm ${colors[config.color]}`}
      style={{ flexShrink: 0 }}
    >
      <div className="flex items-center gap-2">
        <AlertTriangle className="w-4 h-4 flex-shrink-0" />
        <span>{config.msg}</span>
        <a href="/billing" className="underline font-medium ml-2">
          {config.cta} →
        </a>
      </div>
      {config.dismissable && (
        <button
          onClick={() => setDismissed(true)}
          className="ml-4 opacity-70 hover:opacity-100"
          aria-label="Dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      )}
    </div>
  )
}
